import { computeOrderVatBreakdown } from './orderVatCalculations'

const roundToTwo = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.round((n + Number.EPSILON) * 100) / 100
}

/**
 * Универсальная функция для расчета метрик заказов
 * Поддерживает расчеты для одного товара, массива товаров, одного заказа или массива заказов
 *
 * @param {Object|Array} data - Данные для расчета:
 *   - Object (item): { marketPriceCP, ourPrice, quantity, IVA }
 *   - Object (order): { items: [...], paidValue, deliveryPrice }
 *   - Array (items): [{ marketPriceCP, ourPrice, quantity, IVA }, ...]
 *   - Array (orders): [{ items: [...], paidValue, deliveryPrice }, ...]
 *
 * @param {Object} options - Опции расчета:
 *   - includeDelivery: boolean - включать ли deliveryPrice в total (по умолчанию false)
 *   - format: 'number' | 'string' | 'formatted' - формат возврата (по умолчанию 'number')
 *   - currency: string - валюта для форматирования (по умолчанию '€')
 *
 * @returns {Object} Объект с метриками:
 *   - total: number|string - общая сумма продажи
 *   - profit: number|string - прибыль (Total − COGS, без вычитания доли НДС)
 *   - cogs: number|string - себестоимость (COGS)
 *   - balance: number|string - баланс/долг (total - paid)
 *   - paid: number|string - оплаченная сумма
 *   - profitPercent: number|string - процент прибыли
 */
export const calculateOrderMetrics = (
  data,
  options = {},
) => {
  const {
    includeDelivery = false,
    format = 'number',
    currency = '€',
  } = options

  // Вспомогательная функция для форматирования
  const formatValue = (value) => {
    const rounded = roundToTwo(value)
    if (format === 'string') {
      return rounded.toFixed(2) + currency
    } else if (format === 'formatted') {
      return rounded.toFixed(2) + currency
    }
    return rounded
  }

  // Вспомогательная функция для безопасного парсинга
  const safeParse = (value, defaultValue = 0) => {
    // Если значение уже число и валидно
    if (
      typeof value === 'number' &&
      !isNaN(value) &&
      isFinite(value)
    ) {
      return value
    }
    // Если значение null, undefined или пустая строка
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return defaultValue
    }
    // Если значение строка, убираем пробелы и проверяем
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (
        trimmed === '' ||
        trimmed === 'null' ||
        trimmed === 'undefined'
      ) {
        return defaultValue
      }
      const normalized = trimmed
        .replaceAll('€', '')
        .replaceAll('EUR', '')
        .replaceAll(' ', '')
        .replaceAll(',', '.')
      const normalizedParsed = parseFloat(normalized)
      if (
        !isNaN(normalizedParsed) &&
        isFinite(normalizedParsed)
      ) {
        return normalizedParsed
      }
    }
    // Парсим значение (работает и для строк, и для чисел)
    const parsed = parseFloat(value)
    // Проверяем на NaN и Infinity
    if (isNaN(parsed) || !isFinite(parsed)) {
      return defaultValue
    }
    return parsed
  }

  // Расчет для одного товара
  const calculateItemMetrics = (item) => {
    if (!item || typeof item !== 'object') {
      return {
        total: 0,
        cogs: 0,
        profit: 0,
        profitPercent: 0,
      }
    }

    // Парсим все значения с безопасной обработкой
    const marketPrice = safeParse(
      item.marketPriceCP ?? item.marketPrice ?? item.price,
      0,
    )
    const ourPrice = safeParse(item.ourPrice, 0)
    const quantity = safeParse(item.quantity, 1)
    const iva = safeParse(item.IVA, 0)

    // Дополнительная валидация и нормализация значений
    const validMarketPrice =
      typeof marketPrice === 'number' &&
      !isNaN(marketPrice) &&
      isFinite(marketPrice) &&
      marketPrice >= 0
        ? marketPrice
        : 0

    const validOurPrice =
      typeof ourPrice === 'number' &&
      !isNaN(ourPrice) &&
      isFinite(ourPrice) &&
      ourPrice >= 0
        ? ourPrice
        : 0

    const validQuantity =
      typeof quantity === 'number' &&
      !isNaN(quantity) &&
      isFinite(quantity) &&
      quantity > 0
        ? quantity
        : 1

    const validIva =
      typeof iva === 'number' &&
      !isNaN(iva) &&
      isFinite(iva)
        ? Math.max(0, Math.min(100, iva))
        : 0

    // Расчет total и cogs с гарантией валидности
    const totalValue = validMarketPrice * validQuantity
    const cogsValue = validOurPrice * validQuantity
    const total = roundToTwo(totalValue)
    const cogs = roundToTwo(cogsValue)

    // Расчет profit: Total − COGS (без вычитания доли НДС)
    const profitValue = total - cogs
    const profit =
      !isNaN(profitValue) && isFinite(profitValue)
        ? roundToTwo(profitValue)
        : 0

    // Расчет profitPercent с защитой от деления на ноль и NaN
    let profitPercent = 0
    if (
      total > 0 &&
      !isNaN(profit) &&
      isFinite(profit) &&
      !isNaN(total) &&
      isFinite(total)
    ) {
      const percentValue = (profit / total) * 100
      if (!isNaN(percentValue) && isFinite(percentValue)) {
        profitPercent = roundToTwo(percentValue)
      }
    }

    // Финальная проверка всех значений перед возвратом
    return {
      total: isNaN(total) || !isFinite(total) ? 0 : total,
      cogs: isNaN(cogs) || !isFinite(cogs) ? 0 : cogs,
      profit:
        isNaN(profit) || !isFinite(profit) ? 0 : profit,
      profitPercent:
        isNaN(profitPercent) || !isFinite(profitPercent)
          ? 0
          : profitPercent,
    }
  }

  // Расчет для массива товаров
  const calculateItemsMetrics = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
      return {
        total: 0,
        cogs: 0,
        profit: 0,
        profitPercent: 0,
      }
    }

    const metrics = items.reduce(
      (acc, item) => {
        const itemMetrics = calculateItemMetrics(item)
        return {
          total: roundToTwo(acc.total + itemMetrics.total),
          cogs: roundToTwo(acc.cogs + itemMetrics.cogs),
          profit: roundToTwo(
            acc.profit + itemMetrics.profit,
          ),
        }
      },
      { total: 0, cogs: 0, profit: 0 },
    )

    metrics.profitPercent = roundToTwo(
      metrics.total > 0
        ? (metrics.profit / metrics.total) * 100
        : 0,
    )

    return metrics
  }

  // Расчет для одного заказа
  const calculateOrderMetrics = (order) => {
    if (!order || !order.items) {
      return {
        total: 0,
        cogs: 0,
        profit: 0,
        balance: 0,
        paid: 0,
        profitPercent: 0,
      }
    }

    const itemsMetrics = calculateItemsMetrics(order.items)
    const paid = roundToTwo(safeParse(order.paidValue, 0))

    const total = computeOrderVatBreakdown(order, {
      includeDelivery,
    }).gross
    const balance = roundToTwo(total - paid)

    // profitPercent рассчитывается относительно total товаров (без deliveryPrice),
    // так как profit не зависит от deliveryPrice
    const profitPercent = roundToTwo(
      itemsMetrics.total > 0
        ? (itemsMetrics.profit / itemsMetrics.total) * 100
        : 0,
    )

    return {
      total,
      cogs: itemsMetrics.cogs,
      profit: itemsMetrics.profit,
      balance,
      paid,
      profitPercent,
    }
  }

  // Расчет для массива заказов
  const calculateOrdersMetrics = (orders) => {
    if (!Array.isArray(orders) || orders.length === 0) {
      return {
        total: 0,
        cogs: 0,
        profit: 0,
        balance: 0,
        paid: 0,
        profitPercent: 0,
      }
    }

    let totalWithoutDelivery = 0
    let totalWithDelivery = 0
    let cogs = 0
    let paid = 0

    for (const order of orders) {
      const items = Array.isArray(order?.items) ? order.items : []
      let orderItemsTotal = 0
      for (const item of items) {
        const quantity = safeParse(item?.quantity, 1)
        const salePrice = safeParse(
          item?.marketPriceCP ??
            item?.marketPrice ??
            item?.price,
          0,
        )
        const cogsPrice = safeParse(
          item?.ourPrice ?? item?.ourPriceCP,
          0,
        )
        orderItemsTotal += salePrice * quantity
        cogs += cogsPrice * quantity
      }

      const orderGross = computeOrderVatBreakdown(order, {
        includeDelivery,
      }).gross
      const orderGrossNoDelivery = computeOrderVatBreakdown(order, {
        includeDelivery: false,
      }).gross

      totalWithoutDelivery += orderGrossNoDelivery
      totalWithDelivery += orderGross
      paid += safeParse(order?.paidValue, 0)
    }

    const profit = totalWithoutDelivery - cogs
    const total = includeDelivery
      ? totalWithDelivery
      : totalWithoutDelivery
    const balance = total - paid

    return {
      total: roundToTwo(total),
      cogs: roundToTwo(cogs),
      profit: roundToTwo(profit),
      paid: roundToTwo(paid),
      balance: roundToTwo(balance),
      profitPercent: roundToTwo(
        totalWithoutDelivery > 0
          ? (profit / totalWithoutDelivery) * 100
          : 0,
      ),
    }
  }

  // Определяем тип данных и вызываем соответствующую функцию
  let result

  if (Array.isArray(data)) {
    // Массив заказов или товаров
    if (data.length > 0) {
      // Проверяем, является ли это массивом заказов (есть поле items)
      if (data[0]?.items !== undefined) {
        result = calculateOrdersMetrics(data)
      } else {
        // Массив товаров
        result = calculateItemsMetrics(data)
      }
    } else {
      result = {
        total: 0,
        cogs: 0,
        profit: 0,
        balance: 0,
        paid: 0,
        profitPercent: 0,
      }
    }
  } else if (data?.items !== undefined) {
    // Один заказ
    result = calculateOrderMetrics(data)
  } else {
    // Один товар
    result = calculateItemMetrics(data)
    // Для одного товара balance и paid не применимы
    result.balance = 0
    result.paid = 0
  }

  // Форматируем результат
  if (format !== 'number') {
    return {
      total: formatValue(result.total),
      cogs: formatValue(result.cogs),
      profit: formatValue(result.profit),
      balance: formatValue(result.balance),
      paid: formatValue(result.paid),
      profitPercent:
        roundToTwo(result.profitPercent).toFixed(2) + '%',
    }
  }

  // Для формата 'number' также округляем все значения
  return {
    total: roundToTwo(result.total),
    cogs: roundToTwo(result.cogs),
    profit: roundToTwo(result.profit),
    balance: roundToTwo(result.balance),
    paid: roundToTwo(result.paid),
    profitPercent: roundToTwo(result.profitPercent),
  }
}

/**
 * Упрощенные функции для обратной совместимости
 */

// Расчет total для одного товара (замена findTotal)
export const findTotal = (item, options = {}) => {
  const metrics = calculateOrderMetrics(item, {
    ...options,
    format: 'string',
  })
  return metrics.total
}

// Расчет profit для одного товара (замена findProfit)
export const findProfit = (item, options = {}) => {
  const metrics = calculateOrderMetrics(item, {
    ...options,
    format: 'string',
  })
  return metrics.profit
}

/**
 * Маржа по товару: profit / (cost + VAT on cost) * 100, строка с 2 знаками
 * @param {Object} item - { marketPriceCP, ourPrice, quantity, IVA }
 * @returns {string}
 */
export const getItemMargin = (item) => {
  const metrics = calculateOrderMetrics(item, {
    format: 'number',
  })
  const { total, cogs, profit } = metrics
  const iva = parseFloat(item?.IVA) || 0
  const costWithVat = cogs * (1 + iva / 100)
  if (costWithVat <= 0 || !isFinite(costWithVat))
    return '0.00'
  const margin = (profit / costWithVat) * 100
  const result = margin.toFixed(2)
  return isNaN(result) || !isFinite(Number(result))
    ? '0.00'
    : result
}

// Расчет total для заказа (con IVA, aligned with albarán band gross)
export const getOrderTotal = (order, options = {}) => {
  const includeDelivery =
    typeof options?.includeDelivery === 'boolean'
      ? options.includeDelivery
      : true
  return getOrderGrossFromVatBands(order, { includeDelivery })
}

// Расчет profit для заказа
export const getOrderProfit = (order, options = {}) => {
  const metrics = calculateOrderMetrics(order, {
    ...options,
    format: 'number',
  })
  return metrics.profit
}

// Расчет COGS для заказа
export const getOrderCogs = (order, options = {}) => {
  const metrics = calculateOrderMetrics(order, {
    ...options,
    format: 'number',
  })
  return metrics.cogs
}

/**
 * BASE IMPONIBLE — same as albarán footer (per-line round, IVA bands).
 */
export const getOrderGoodsWoTva = (order, options = {}) => {
  const { includeDelivery = true } = options
  return computeOrderVatBreakdown(order, { includeDelivery }).base
}

/**
 * TVA total — sum of round(bandBase × rate%) per IVA band (albarán footer).
 */
export const getOrderTaxAmount = (order, options = {}) => {
  const { includeDelivery = true } = options
  return computeOrderVatBreakdown(order, { includeDelivery }).tax
}

/** Gross total con IVA from VAT bands (albarán con-IVA footer). */
export const getOrderGrossFromVatBands = (order, options = {}) => {
  const { includeDelivery = true } = options
  return computeOrderVatBreakdown(order, { includeDelivery }).gross
}

// Расчет balance для заказа
export const getOrderBalance = (order, options = {}) => {
  const includeDelivery =
    typeof options?.includeDelivery === 'boolean'
      ? options.includeDelivery
      : true
  const total = getOrderGrossFromVatBands(order, { includeDelivery })
  const paid = getOrderPaid(order, options)
  return roundToTwo(total - paid)
}

// Расчет paid для заказа
export const getOrderPaid = (order, options = {}) => {
  const metrics = calculateOrderMetrics(order, {
    ...options,
    format: 'number',
  })
  return metrics.paid
}

// Агрегированные расчеты для массива заказов
export const getOrdersTotal = (orders, options = {}) => {
  const includeDelivery =
    typeof options?.includeDelivery === 'boolean'
      ? options.includeDelivery
      : true
  const metrics = calculateOrderMetrics(orders, {
    ...options,
    includeDelivery,
    format: 'string',
  })
  return metrics.total
}

export const getOrdersProfit = (orders, options = {}) => {
  const metrics = calculateOrderMetrics(orders, {
    ...options,
    format: 'string',
  })
  return metrics.profit
}

export const getOrdersCogs = (orders, options = {}) => {
  const metrics = calculateOrderMetrics(orders, {
    ...options,
    format: 'string',
  })
  return metrics.cogs
}

export const getOrdersBalance = (orders, options = {}) => {
  const includeDelivery =
    typeof options?.includeDelivery === 'boolean'
      ? options.includeDelivery
      : true
  const metrics = calculateOrderMetrics(orders, {
    ...options,
    includeDelivery,
    format: 'string',
  })
  return metrics.balance
}

export const getOrdersPaid = (orders, options = {}) => {
  const metrics = calculateOrderMetrics(orders, {
    ...options,
    format: 'string',
  })
  return metrics.paid
}
